import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Users,
  Phone,
  Mail,
  MapPin,
  Building2,
  Calendar,
  DollarSign,
  FileText,
  Plus,
  Eye,
  CreditCard,
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Download,
  TrendingUp,
  Heart,
  MessageCircle,
  Send,
  Banknote
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

const TIPOS_ALIADO = {
  wedding_planner: 'Wedding Planner',
  hotel: 'Hotel',
  hacienda: 'Hacienda',
  fotografo: 'Fotógrafo',
  organizador_eventos: 'Organizador de Eventos',
  productora: 'Productora',
  proveedor: 'Proveedor',
  aliado_bodas: 'Aliado del Sector Bodas',
  otro: 'Otro'
};

const ESTADOS_ALIADO = {
  activo: { label: 'Activo', color: 'bg-green-500/20 text-green-400' },
  inactivo: { label: 'Inactivo', color: 'bg-zinc-500/20 text-zinc-400' },
  suspendido: { label: 'Suspendido', color: 'bg-red-500/20 text-red-400' }
};

const ESTADOS_REFERIDO = {
  registrado: { label: 'Registrado', color: 'bg-zinc-600' },
  pendiente_contacto: { label: 'Pendiente', color: 'bg-amber-600' },
  contactado: { label: 'Contactado', color: 'bg-blue-600' },
  cotizado: { label: 'Cotizado', color: 'bg-purple-600' },
  venta_realizada: { label: 'Venta', color: 'bg-green-600' },
  no_fue_venta: { label: 'No venta', color: 'bg-red-600' },
  listo_cobro: { label: 'Listo cobro', color: 'bg-[#D4AF37]' },
  pagado: { label: 'Pagado', color: 'bg-emerald-600' }
};

// Componente para cada fila de comisión con acciones
const ComisionRow = ({ referido, aliado, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleAprobar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/aliados/comisiones/${referido.id}/aprobar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Comisión aprobada exitosamente');
      
      // Abrir WhatsApp automáticamente
      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
      
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar comisión');
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  const handleMarcarPagada = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/aliados/comisiones/${referido.id}/estado`,
        { estado_comision: 'pagada' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Comisión marcada como pagada');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  const handleEnviarWhatsApp = async (tipo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/aliados/comisiones/${referido.id}/notificar-whatsapp?tipo_notificacion=${tipo}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      } else {
        toast.error('El aliado no tiene número de WhatsApp configurado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar mensaje');
    }
    setShowMenu(false);
  };

  const estadoComision = referido.estado_comision || 'pendiente';

  return (
    <tr className="hover:bg-zinc-800/30 relative">
      <td className="py-3 px-4">{referido.nombre}</td>
      <td className="py-3 px-4 text-sm text-zinc-400 capitalize">{referido.tipo_referido}</td>
      <td className="py-3 px-4 text-right">{formatPrice(referido.valor_cotizacion)}</td>
      <td className="py-3 px-4 text-center text-[#D4AF37]">{referido.porcentaje_comision || 0}%</td>
      <td className="py-3 px-4 text-right font-medium">{formatPrice(referido.valor_comision)}</td>
      <td className="py-3 px-4 text-center">
        <span className={`px-2 py-0.5 rounded-full text-xs ${
          estadoComision === 'pagada' ? 'bg-green-500/20 text-green-400' :
          estadoComision === 'aprobada' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
          'bg-amber-500/20 text-amber-400'
        }`}>
          {estadoComision}
        </span>
      </td>
      <td className="py-3 px-4 text-center">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            disabled={loading}
            className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            ) : (
              <MessageCircle className="w-4 h-4 text-zinc-400" />
            )}
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 z-50 w-56 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl py-1">
                {estadoComision === 'pendiente' || estadoComision === 'calculada' ? (
                  <button
                    onClick={handleAprobar}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-800 text-sm transition-colors"
                  >
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Aprobar y Notificar</span>
                  </button>
                ) : null}
                
                {estadoComision === 'aprobada' && (
                  <>
                    <button
                      onClick={handleMarcarPagada}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-800 text-sm transition-colors"
                    >
                      <Banknote className="w-4 h-4 text-green-500" />
                      <span>Marcar como Pagada</span>
                    </button>
                    <button
                      onClick={() => handleEnviarWhatsApp('pago')}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-800 text-sm transition-colors"
                    >
                      <Send className="w-4 h-4 text-green-500" />
                      <span>Notificar Pago</span>
                    </button>
                  </>
                )}
                
                <div className="border-t border-zinc-800 my-1" />
                
                <button
                  onClick={() => handleEnviarWhatsApp('recordatorio')}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-800 text-sm transition-colors"
                >
                  <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
                  <span>Enviar Recordatorio</span>
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const AliadoDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aliado, setAliado] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [referidos, setReferidos] = useState([]);
  const [comisiones, setComisiones] = useState(null);
  const [weddings, setWeddings] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const fetchAliado = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAliado(response.data);
    } catch (error) {
      toast.error('Error al cargar el aliado');
      navigate('/aliados');
    }
  }, [id, navigate]);

  const fetchReferidos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${id}/referidos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferidos(response.data.referidos || []);
    } catch (error) {
      console.debug('Error fetching referidos');
    }
  }, [id]);

  const fetchComisiones = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${id}/comisiones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComisiones(response.data);
    } catch (error) {
      console.debug('Error fetching comisiones');
    }
  }, [id]);

  const fetchWeddings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${id}/weddings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeddings(response.data.weddings || []);
    } catch (error) {
      console.debug('Error fetching weddings');
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAliado();
      await Promise.all([fetchReferidos(), fetchComisiones(), fetchWeddings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAliado, fetchReferidos, fetchComisiones, fetchWeddings]);

  const handleCrearUsuario = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/aliados/${id}/crear-usuario?password=${encodeURIComponent(newPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Usuario creado exitosamente');
      setCreatingUser(false);
      setNewPassword('');
      fetchAliado();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!aliado) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <p>Aliado no encontrado</p>
      </div>
    );
  }

  const estadoInfo = ESTADOS_ALIADO[aliado.estado] || ESTADOS_ALIADO.activo;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-black border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/aliados')}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Aliados</span>
              </button>
              <div className="h-6 w-px bg-zinc-800" />
              <h1 className="font-display text-lg truncate">{aliado.nombre}</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estadoInfo.color}`}>
                {estadoInfo.label}
              </span>
            </div>
            
            <div className="flex gap-2">
              {!aliado.id_usuario && user?.role !== 'aliado' && (
                <button
                  onClick={() => setCreatingUser(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/10 transition-colors text-sm"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Crear Usuario</span>
                </button>
              )}
              <button
                onClick={() => navigate(`/aliados/${id}/editar`)}
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Editar</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Modal Crear Usuario */}
      {creatingUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="font-display text-xl mb-4">Crear Usuario de Acceso</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Se creará un usuario con el correo <strong className="text-white">{aliado.correo}</strong> 
              y rol <strong className="text-[#D4AF37]">Aliado</strong>.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Contraseña</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setCreatingUser(false); setNewPassword(''); }}
                className="flex-1 px-4 py-2 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCrearUsuario}
                className="flex-1 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors font-medium"
              >
                Crear Usuario
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-display">{aliado.total_referidos || 0}</p>
              <p className="text-xs text-zinc-400">Referidos</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-display">{aliado.total_ventas || 0}</p>
              <p className="text-xs text-zinc-400">Ventas</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-display text-[#D4AF37]">{formatPrice(aliado.total_comisiones_generadas || 0)}</p>
              <p className="text-xs text-zinc-400">Comisiones Generadas</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-display text-green-400">{formatPrice(aliado.total_comisiones_pagadas || 0)}</p>
              <p className="text-xs text-zinc-400">Comisiones Pagadas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'info', label: 'Información', icon: Building2 },
              { id: 'referidos', label: 'Referidos', icon: Users },
              { id: 'comisiones', label: 'Comisiones', icon: DollarSign },
              { id: 'weddings', label: 'Weddings', icon: Heart },
              { id: 'cobro', label: 'Datos Cobro', icon: CreditCard }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#D4AF37] text-[#D4AF37]'
                    : 'border-transparent text-zinc-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab: Información */}
        {activeTab === 'info' && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-display text-lg mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#D4AF37]" />
                Datos del Aliado
              </h3>
              <div className="space-y-4">
                <div>
                  <span className="text-zinc-400 text-sm">Tipo de Aliado</span>
                  <p className="font-medium">{TIPOS_ALIADO[aliado.tipo_aliado] || aliado.tipo_aliado}</p>
                </div>
                <div>
                  <span className="text-zinc-400 text-sm">Documento</span>
                  <p className="font-medium">{aliado.tipo_documento?.toUpperCase()} {aliado.numero_documento}</p>
                </div>
                {aliado.direccion && (
                  <div>
                    <span className="text-zinc-400 text-sm">Dirección</span>
                    <p className="font-medium">{aliado.direccion}</p>
                  </div>
                )}
                {aliado.observaciones && (
                  <div>
                    <span className="text-zinc-400 text-sm">Observaciones</span>
                    <p className="font-medium">{aliado.observaciones}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-display text-lg mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#D4AF37]" />
                Contacto
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-zinc-400" />
                  <div>
                    <span className="text-zinc-400 text-sm">Teléfono</span>
                    <p className="font-medium">{aliado.telefono}</p>
                  </div>
                </div>
                {aliado.whatsapp && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-green-500" />
                    <div>
                      <span className="text-zinc-400 text-sm">WhatsApp</span>
                      <p className="font-medium">{aliado.whatsapp}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-zinc-400" />
                  <div>
                    <span className="text-zinc-400 text-sm">Correo</span>
                    <p className="font-medium">{aliado.correo}</p>
                  </div>
                </div>
                {aliado.ciudad && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-zinc-400" />
                    <div>
                      <span className="text-zinc-400 text-sm">Ciudad</span>
                      <p className="font-medium">{aliado.ciudad}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Usuario de acceso */}
            <div className="md:col-span-2 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-display text-lg mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#D4AF37]" />
                Acceso al Sistema
              </h3>
              {aliado.id_usuario ? (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <p>Usuario de acceso configurado - <span className="text-[#D4AF37]">{aliado.correo}</span></p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <p className="text-zinc-400">Sin usuario de acceso configurado</p>
                  </div>
                  {user?.role !== 'aliado' && (
                    <button
                      onClick={() => setCreatingUser(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C49F27] transition-colors text-sm font-medium"
                    >
                      <UserPlus className="w-4 h-4" />
                      Crear Usuario
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Referidos */}
        {activeTab === 'referidos' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-lg">Referidos ({referidos.length})</h3>
              <button
                onClick={() => navigate(`/aliados/${id}/referidos/nuevo`)}
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Nuevo Referido
              </button>
            </div>
            
            {referidos.length > 0 ? (
              <div className="space-y-3">
                {referidos.map((referido) => {
                  const estadoRef = ESTADOS_REFERIDO[referido.estado] || ESTADOS_REFERIDO.registrado;
                  return (
                    <div
                      key={referido.id}
                      className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors cursor-pointer"
                      onClick={() => navigate(`/aliados/${id}/referidos/${referido.id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <p className="font-medium">{referido.nombre}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs text-white ${estadoRef.color}`}>
                              {estadoRef.label}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-400 mt-1">
                            {referido.tipo_referido === 'novio' ? 'Novio' : 
                             referido.tipo_referido === 'invitado' ? 'Invitado' : 
                             referido.tipo_referido === 'mascota' ? 'Mascota' : referido.tipo_referido}
                            {referido.telefono && ` - ${referido.telefono}`}
                          </p>
                        </div>
                        <div className="text-right">
                          {referido.valor_cotizacion > 0 && (
                            <p className="text-sm text-zinc-400">Cotización: {formatPrice(referido.valor_cotizacion)}</p>
                          )}
                          {referido.valor_comision > 0 && (
                            <p className="text-[#D4AF37] font-medium">Comisión: {formatPrice(referido.valor_comision)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Users className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
                <p className="text-zinc-400 mb-4">No hay referidos registrados</p>
                <button
                  onClick={() => navigate(`/aliados/${id}/referidos/nuevo`)}
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Agregar Referido
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tab: Comisiones */}
        {activeTab === 'comisiones' && (
          <div>
            {/* Header con botón de generar cuenta de cobro */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-lg">Comisiones</h3>
              {comisiones && comisiones.referidos?.filter(r => r.estado_comision === 'aprobada' || r.estado_comision === 'calculada').length > 0 && (
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${API_URL}/api/aliados/${id}/cuenta-cobro/generar`,
                        {},
                        { 
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        }
                      );
                      
                      // Descargar el PDF
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `cuenta_cobro_${aliado.nombre.replace(/\s+/g, '_')}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      
                      toast.success('Cuenta de cobro generada exitosamente');
                      fetchComisiones();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Error al generar cuenta de cobro');
                    }
                  }}
                  className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Generar Cuenta de Cobro
                </button>
              )}
            </div>

            {/* Resumen */}
            {comisiones && (
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-display text-[#D4AF37]">{formatPrice(comisiones.total_comisiones)}</p>
                  <p className="text-xs text-zinc-400">Total Generado</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-display text-amber-400">{formatPrice(comisiones.total_pendientes)}</p>
                  <p className="text-xs text-zinc-400">Pendiente de Pago</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-display text-green-400">{formatPrice(comisiones.total_pagadas)}</p>
                  <p className="text-xs text-zinc-400">Ya Pagado</p>
                </div>
              </div>
            )}

            {/* Lista de comisiones */}
            {comisiones && comisiones.referidos?.length > 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium text-sm">Referido</th>
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium text-sm">Tipo</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium text-sm">Cotización</th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium text-sm">%</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium text-sm">Comisión</th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium text-sm">Estado</th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {comisiones.referidos.map((ref) => (
                      <ComisionRow 
                        key={ref.id} 
                        referido={ref} 
                        aliado={aliado}
                        onUpdate={fetchComisiones}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <DollarSign className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
                <p className="text-zinc-400">No hay comisiones generadas aún</p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Weddings */}
        {activeTab === 'weddings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-lg">Weddings y Eventos ({weddings.length})</h3>
            </div>
            
            {weddings.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {weddings.map((wedding) => (
                  <div key={wedding.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-pink-500/20 rounded-lg">
                        <Heart className="w-5 h-5 text-pink-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{wedding.nombre_evento}</p>
                        {wedding.nombre_novios && (
                          <p className="text-sm text-zinc-400">{wedding.nombre_novios}</p>
                        )}
                        {wedding.fecha_evento && (
                          <p className="text-sm text-[#D4AF37] mt-1">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {new Date(wedding.fecha_evento).toLocaleDateString('es-CO')}
                          </p>
                        )}
                        {wedding.lugar && (
                          <p className="text-sm text-zinc-500 mt-1">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {wedding.lugar}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Heart className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
                <p className="text-zinc-400">No hay weddings registradas</p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Datos de Cobro */}
        {activeTab === 'cobro' && (
          <div className="max-w-2xl">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-display text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                  Datos Bancarios para Cobro
                </h3>
                <button
                  onClick={() => navigate(`/aliados/${id}/editar`)}
                  className="text-[#D4AF37] text-sm hover:underline"
                >
                  Editar
                </button>
              </div>
              
              {aliado.datos_cobro && Object.keys(aliado.datos_cobro).length > 0 ? (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-zinc-400 text-sm">Titular de la Cuenta</span>
                      <p className="font-medium">{aliado.datos_cobro.titular_cuenta || '-'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Documento del Titular</span>
                      <p className="font-medium">
                        {aliado.datos_cobro.tipo_documento_titular?.toUpperCase()} {aliado.datos_cobro.documento_titular || '-'}
                      </p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Banco</span>
                      <p className="font-medium">{aliado.datos_cobro.banco || '-'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Tipo de Cuenta</span>
                      <p className="font-medium capitalize">{aliado.datos_cobro.tipo_cuenta || '-'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Número de Cuenta</span>
                      <p className="font-medium">{aliado.datos_cobro.numero_cuenta || '-'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Llave de Pago (Nequi/Daviplata)</span>
                      <p className="font-medium">{aliado.datos_cobro.llave_pago || '-'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Método de Pago Preferido</span>
                      <p className="font-medium">{aliado.datos_cobro.metodo_pago || '-'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm">Teléfono de Pago</span>
                      <p className="font-medium">{aliado.datos_cobro.telefono_pago || '-'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-zinc-400 text-sm">Correo de Notificaciones de Pago</span>
                      <p className="font-medium">{aliado.datos_cobro.correo_pago || '-'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
                  <p className="text-zinc-400 mb-4">No hay datos bancarios configurados</p>
                  <button
                    onClick={() => navigate(`/aliados/${id}/editar`)}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Configurar Datos
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AliadoDetail;
